import { HTMLInputTypeAttribute } from 'react';
import {
  OutlinedInput,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import InputWrapper from '../InputWrapper';
import { important, sxToObject } from '../../../../utils/global/helper';
import { getInputBorderStyle } from '../../../../helpers/styles';

interface Props {
  id: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  type?: HTMLInputTypeAttribute;
  tooltip?: string;
  suffix?: string;
  min?: number | Date;
  max?: number | Date;
  step?: number;
  value: string | Date;
  onChange: (value: string) => void;
  onBlur?: () => void;
  inputSx?: SxProps<Theme>;
  isError?: boolean;
  isLabelMultiline?: boolean;
  required?: boolean;
  autoFocus?: boolean;
}

const getDateTimeFormat = (value: number | string | Date) =>
  format(new Date(value), "yyyy-MM-dd'T'HH:mm");

function TextInputControlled({
  id,
  label,
  placeholder,
  disabled,
  type,
  value,
  onChange,
  onBlur,
  tooltip,
  suffix,
  min,
  max,
  step,
  inputSx,
  isError,
  isLabelMultiline,
  required,
  autoFocus,
}: Props) {
  const theme = useTheme();

  const val =
    type === 'datetime-local' && value ? getDateTimeFormat(value) : value;
  const minVal =
    type === 'datetime-local' && min ? getDateTimeFormat(min) : undefined;
  const maxVal =
    type === 'datetime-local' && max ? getDateTimeFormat(max) : undefined;

  return (
    <InputWrapper
      label={label || ''}
      name={id.toString()}
      tooltip={tooltip}
      isLabelMultiline={isLabelMultiline}
      required={required}>
      <OutlinedInput
        id={id.toString()}
        type={type}
        name={id.toString()}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        onBlur={onBlur || undefined}
        sx={{
          width: '100%',
          height: important('44px'),
          fontSize: 'body2.fontSize',
          bgcolor: theme.palette.background.default,
          '& .MuiOutlinedInput-notchedOutline': getInputBorderStyle({
            isError,
            theme,
          }),
          ...sxToObject(inputSx, theme),
        }}
        endAdornment={<Typography variant="body2">{suffix}</Typography>}
        inputProps={{ min: minVal, max: maxVal, step }}
        value={val}
        onChange={e => onChange(e.target.value)}
      />
    </InputWrapper>
  );
}

export default TextInputControlled;
